import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ToastProvider';
import { phoneMask, cpfMask, unmask } from '../../lib/masks';
import { SEOHead, PageSEO } from '../../components/SEOHead';

export function Register(){
  const { register } = useAuth();
  const nav = useNavigate();
  const [email,setEmail] = useState('');
  const [password,setPassword] = useState('');
  const [name,setName] = useState('');
  const [birthDate,setBirthDate] = useState('');
  const [phone,setPhone] = useState('');
  const [cpf,setCpf] = useState('');
  const [error,setError] = useState<string| null>(null);
  const [loading,setLoading] = useState(false);
  const { push } = useToast();

  async function handle(e:React.FormEvent){
    e.preventDefault(); setError(null); setLoading(true);
    try {
      await register({
        email,
        password,
        name,
        birthDate,
        phone: phone ? unmask(phone) : undefined,
        cpf: cpf ? unmask(cpf) : undefined
      });
      push('Conta criada. Bem-vindo!','success');
      nav('/app');
    } catch(err:any){
      setError(err.message);
      push(err.message,'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
    <SEOHead {...PageSEO.register} />
    <AuthShell title="Comece gratuitamente" subtitle="Crie sua conta e ganhe créditos para testar">
      <form onSubmit={handle} className="space-y-4">
        {/* Nome completo */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Nome completo <span className="text-red-400">*</span>
          </label>
          <input
            className="input w-full"
            value={name}
            onChange={e=>setName(e.target.value)}
            type="text"
            placeholder="Seu nome completo"
            required
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Email <span className="text-red-400">*</span>
          </label>
          <input
            className="input w-full"
            value={email}
            onChange={e=>setEmail(e.target.value)}
            type="email"
            placeholder="seu@email.com"
            required
          />
        </div>

        {/* Data de nascimento */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Data de nascimento <span className="text-red-400">*</span>
          </label>
          <input
            className="input w-full"
            value={birthDate}
            onChange={e=>setBirthDate(e.target.value)}
            type="date"
            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
            required
          />
          <p className="text-xs opacity-60 mt-1">Você deve ter no mínimo 18 anos</p>
        </div>

        {/* Telefone (opcional) */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Telefone <span className="text-xs opacity-60">(opcional)</span>
          </label>
          <input
            className="input w-full"
            value={phone}
            onChange={e=>setPhone(phoneMask(e.target.value))}
            type="tel"
            placeholder="(11) 99999-9999"
            maxLength={15}
          />
        </div>

        {/* CPF (opcional) */}
        <div>
          <label className="block text-sm font-medium mb-2">
            CPF <span className="text-xs opacity-60">(opcional)</span>
          </label>
          <input
            className="input w-full"
            value={cpf}
            onChange={e=>setCpf(cpfMask(e.target.value))}
            type="text"
            placeholder="000.000.000-00"
            maxLength={14}
          />
        </div>

        {/* Senha */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Senha <span className="text-red-400">*</span>
          </label>
          <input
            className="input w-full"
            value={password}
            onChange={e=>setPassword(e.target.value)}
            type="password"
            placeholder="Mínimo 6 caracteres"
            minLength={6}
            required
          />
          <p className="text-xs opacity-60 mt-1">Use no mínimo 6 caracteres</p>
        </div>

        {error && (
          <div className="panel-alt p-3 flex items-start gap-2 text-sm" style={{background:'var(--cg-danger-bg)', borderColor:'var(--cg-danger)'}}>
            <i className="fa-solid fa-triangle-exclamation mt-0.5" style={{color:'var(--cg-danger)'}}></i>
            <span style={{color:'var(--cg-danger)'}}>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary w-full h-12 text-base"
        >
          {loading ? (
            <><i className="fa-solid fa-spinner fa-spin"></i> Criando conta...</>
          ) : (
            <><i className="fa-solid fa-rocket"></i> Criar Conta Grátis</>
          )}
        </button>

        <div className="text-center text-xs pt-2">
          <span className="opacity-70">Já tem uma conta? </span>
          <Link to="/login" className="text-brand-400 hover:text-brand-300 transition-colors font-medium">
            Fazer login →
          </Link>
        </div>
      </form>
    </AuthShell>
    </>
  );
}

function AuthShell({children,title,subtitle}:{children:React.ReactNode; title:string; subtitle?:string}){
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-3xl"></div>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 justify-center mb-8 mt-6 group">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
            <i className="fa-solid fa-wand-magic-sparkles text-white"></i>
          </div>
          <span className="font-bold text-xl">CopyGenius</span>
        </Link>

        <div className="panel p-8 backdrop-blur-sm">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2 heading">{title}</h1>
            {subtitle && <p className="text-sm opacity-70">{subtitle}</p>}
          </div>
          {children}
        </div>

        <p className="text-center text-xs opacity-60 mt-6">
          © {new Date().getFullYear()} CopyGenius • Copy profissional em segundos
        </p>
      </div>
    </div>
  );
}
